import React, { useEffect, useState } from 'react';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';

import queryString from 'query-string';
import moment from 'moment';

import { isEmpty } from 'lodash';

import BobPage from '../../../components/BobPage';
import TableContainer from './TableContainer';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import KpiPanel from '../../../components/KpiPanel';
import BobTimeStampFilter from '../../../components/RuleExceptionsTable/BobTimestampFilter';
import Navigation from '../Navigation/Navigation';
import HtmlTooltip from '../../../components/HtmlTooltip/HtmlTooltip';
import {
    renderScoreData,
    renderFaultData
} from '../../../helpers/kpiHelpers';

import * as ArchitectActions from '../../../actions/architectActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as bobActions from '../../../actions/bobActions';

import classes from './EquipmentPage.module.css';

const EquipmentPage = ({
    activeConfiguration,
    activeNavCompany,
    activeNavEquipment,
    activeNavSite,
    allSensors,
    clearEntityScoresData,
    clearEntitySensorData,
    companyInfo,
    dashboardEndTimeStamp,
    dashboardIsCurrentTimeStamp,
    dashboardStartTimeStamp,
    dashboardTimeframeEnd,
    dashboardTimeframeStart,
    dashboardTimePeriod,
    desiredInterval,
    equipmentKpis,
    faultHistoryMeta,
    fetchEntityRules,
    fetchEntitySensors,
    fetchEntitySensorData,
    fetchFaultHistory,
    fetchFaultOverlay,
    fetchFaultSummary,
    fetchKpiData,
    fetchRawUnits,
    fetchRuleScoreKpis,
    fetchScoringData,
    fetchConfigEntitesBySiteId,
    history,
    selectedRule,
    selectedRuleSensors,
    selectedSensors,
    selectedWeatherSensors,
    setAllSensors,
    setBobLoading,
    sensorConfiguration,
    setDashboardEndTimestamp,
    setDashboardStartTimestamp,
    setDashboardCurrentTimestamp,
    setDashboardTimeframeEnd,
    setDashboardTimeframeStart,
    setEntityRules,
    setEntityScoringData,
    setFaultData,
    setFaultHistory,
    setFaultSummary,
    setRuleScoringConfiguration,
    setSelectedRule,
    setSelectedRuleSensors,
    setSelectedSensor,
    setSensorConfiguration,
    setSelectedSensors,
    targetNavEquipment,
    ...props
}) => {
    const [axes, setAxes] = useState([]);
    const [equipmentName, setEquipmentName] = useState();
    const [activeTab, setActiveTab] = useState('');

    const queryValues = queryString.parse(history.location.search);
    const equipmentId = queryValues.equipment;

    const [refreshTab, setRefreshTab] = useState('');

    useEffect(() => {
        const { pathname } = history.location;

        if ( !queryValues.appId) queryValues.appId = 'ad356bf2-b9db-4bb5-922e-c3394b1a63e3';
        // set id's if not exist for BOB menu
        if ( queryValues.appId === 'ad356bf2-b9db-4bb5-922e-c3394b1a63e3' ) {
            if ( !queryValues.parentId) queryValues.parentId = '7a88bd3d-64e3-4af9-aa06-9a31533df83c';
            if ( !queryValues.childId) queryValues.childId = 'c49ee7a9-6a28-4820-967c-6c6a9652a4c8'; // Default to Faults view
        }
        
        if (!queryValues.company) queryValues.company = companyInfo.companyId;

        const queryValuesString = queryString.stringify(queryValues);

        history.push(`${pathname}?${queryValuesString}`);
    }, [companyInfo]);

    useEffect(() => {
        clearEntityScoresData();
    }, []);

    useEffect(() => {
        return () => {
            setFaultSummary([], {});
            setFaultHistory([], {});
            setEntityRules([], {});
            setAllSensors([], {});
            setSensorConfiguration({});
            setEntityScoringData([]);
            setSelectedRuleSensors([]);
            setSelectedRule({});
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            await fetchConfigEntitesBySiteId(activeNavSite.id, { page: 1, limit: 1000 });
        };

        if (activeNavSite.id) init();
    }, [activeNavSite]);

    useEffect(() => {
        const init = async () => {
            let promises = createPromises();
            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        if (companyInfo.companyId && activeNavEquipment.id) init();
    }, [dashboardEndTimeStamp, dashboardStartTimeStamp, desiredInterval]);

    useEffect(() => {
        const refresh = async () => {

            if (dashboardTimePeriod === 'Custom') {
                const promises = createPromises();
                await Promise.all(promises.map(p => p.catch(err => err)));
            }
            else {
                await updateTimeStamp(refreshTab);
            }

            setRefreshTab('');
        };

        if (refreshTab === 'sensorgraph' || refreshTab === 'sensorgrid' || refreshTab === 'scoregraph' || refreshTab === 'scoregrid' || refreshTab === 'rawdata') {
            refresh();
        } 
    }, [refreshTab]);

    useEffect(() => {
        const init = async () => {
            let promises = createPromises();
            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        if (companyInfo.companyId && activeNavEquipment.id && isEmpty(activeConfiguration) && !isEmpty(sensorConfiguration)) init();
    }, [sensorConfiguration]);

    const updateTimeStamp = async (tabView) => {
        if (dashboardTimePeriod === 'Current') {
            const endTimeStamp = moment().local();
            const startTimeStamp = moment().local();

            setDashboardStartTimestamp(startTimeStamp);
            setDashboardEndTimestamp(endTimeStamp);
            setDashboardCurrentTimestamp(moment().local());
        } else if (dashboardTimePeriod === 'Previous') {
            const endTimeStamp = moment().local();
            const interval = tabView && tabView.toLowerCase().includes('score') ? 15 : desiredInterval;
            const minutes = interval === 1 ? 240 : 1440;
            const startTimeStamp = interval >= 5 ?
                moment().set('minute', Math.floor(moment().minute() / interval) * interval).subtract(minutes, 'm').local()
                : moment().subtract(minutes, 'm').local();

            setDashboardStartTimestamp(startTimeStamp);
            setDashboardEndTimestamp(endTimeStamp);
        }
    };

    const createPromises = () => {
        let weatherSensors = '';
        if (!isEmpty(selectedWeatherSensors)) {
            weatherSensors = activeNavSite.address.postalCode + ':' + selectedWeatherSensors.join(':');
        }

        let promises = [];

        promises.push(fetchKpiData(activeNavCompany.id, 'Equipment', equipmentId, dashboardStartTimeStamp, dashboardEndTimeStamp));
        promises.push(fetchRuleScoreKpis(activeNavCompany.id, queryValues.equipment, {From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Equipment'}));

        if (refreshTab !== 'rawdata') {
            if (!isEmpty(selectedRule)) {
                if (!dashboardIsCurrentTimeStamp) {
                    promises.push(fetchFaultOverlay(activeNavCompany.id, activeNavEquipment.id, selectedRule.ruleId, dashboardStartTimeStamp, dashboardEndTimeStamp));
                }
                promises.push(fetchScoringData(activeNavCompany.id, selectedRuleSensors, [selectedRule.ruleId], dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, {EquipmentId: queryValues.equipment, weatherSensors}));
            }
    
            if (!isEmpty(selectedSensors) || !isEmpty(selectedWeatherSensors) || !isEmpty(selectedRuleSensors)) {
                promises.push(fetchEntitySensorData(!isEmpty(selectedSensors) ? selectedSensors : selectedRuleSensors, dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, {axes, entityId: activeNavEquipment.id, weatherSensors}));
            }
        }
        return promises;
    };

    useEffect(() => {
        const init = async () => {
            const queryValues = queryString.parse(history.location.search);
            let promises = [];
            setFaultSummary([], {});
            setFaultHistory([], {});
            setSelectedSensors([]);
            setSelectedRuleSensors([]);
            setSelectedRule({});
            setEntityRules([], {});
            setEntityScoringData([]);
            setRuleScoringConfiguration({ current: {}, previous: [] });

            let weatherSensors = '';
            if (!isEmpty(selectedWeatherSensors)) {
                weatherSensors = activeNavSite.address.postalCode + ':' + selectedWeatherSensors.join(':');
            }

            if (!isEmpty(history.location.state)) {
                if (!isEmpty(history.location.state.sensorHistory)) {
                    const sensorHistory = history.location.state.sensorHistory;
                    const entityId = history.location.state.entityId;

                    if (entityId === activeNavEquipment.id) {
                        setSelectedSensors(sensorHistory);
                        setSelectedRuleSensors(sensorHistory);

                        promises.push(fetchEntitySensorData(sensorHistory, dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { axes, entityId: activeNavEquipment.id, weatherSensors }));
                    }
                }

                if (!isEmpty(history.location.state.ruleHistory)) {
                    const ruleHistory = history.location.state.ruleHistory;
                    const sensorHistory = history.location.state.sensorHistory;

                    setSelectedRule(ruleHistory);
                    promises.push(fetchScoringData(queryValues.company, sensorHistory, [ruleHistory.ruleId], dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, { EquipmentId: activeNavEquipment.id, weatherSensors }));
                }

                let state = { ...history.location.state };
                delete state.ruleHistory;
                delete state.sensorHistory;
                history.replace({ ...history.location, state });
            }

            promises.push(fetchKpiData(activeNavCompany.id, 'Equipment', equipmentId, dashboardStartTimeStamp, dashboardEndTimeStamp));

            promises.push(fetchEntitySensors(activeNavSite.id, activeNavEquipment.id, { mutateCompanyId: true }));
            promises.push(fetchRuleScoreKpis(activeNavCompany.id, activeNavEquipment.id, { From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Equipment', weatherSensors }));

            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        if (activeNavEquipment.id) init();
    }, [activeNavEquipment.id]);

    const setTimeframe = (start, end) => {
        Promise.all([
            setDashboardTimeframeStart(moment(start)),
            setDashboardTimeframeEnd(moment(end))
        ].map(p => p.catch(err => err)));
    };

    useEffect(() => {
        const init = async () => {
            setFaultData({});
            setBobLoading('FaultFetching');
            const index = getTimeframeIndex(dashboardTimeframeStart, dashboardTimeframeEnd);

            let startTime = moment().local();
            let endTime = moment().local();

            if (!dashboardIsCurrentTimeStamp) {
                startTime = moment(dashboardStartTimeStamp);
                endTime = moment().local();
                moment(dashboardStartTimeStamp).add(4, 'hours') >= moment(dashboardEndTimeStamp) ?
                    endTime = moment(endTime)
                    :   
                    endTime = moment(dashboardStartTimeStamp).add(4, 'hours');
            }

            setTimeframe(startTime, endTime);

            let promises = [fetchFaultSummary(activeNavCompany.id, activeNavEquipment.id, dashboardStartTimeStamp, dashboardEndTimeStamp),
                fetchFaultHistory(activeNavCompany.id, activeNavEquipment.id, moment(startTime).format(), moment(endTime).format(), index)];

            await Promise.all(promises.map(p => p.catch(err => err)));
            setBobLoading('FaultFetching');
        };

        if (activeNavEquipment.id) init();
    }, [activeNavEquipment.id]);

    const getTimeframeIndex = (start, end) => {
        const startDifference = moment.duration(moment(start).seconds(0).milliseconds(0).diff(moment(dashboardStartTimeStamp).seconds(0).milliseconds(0)));

        const startHours = startDifference.asHours();
        let index = startHours / 4 >> 0;

        const diff = moment.duration(moment(end).seconds(0).milliseconds(0).diff(moment(start).seconds(0).milliseconds(0)));
        if (diff.asHours() > 4) index++;

        return index;
    };

    useEffect(() => {
        clearEntitySensorData();
        clearEntityScoresData();
        setSelectedSensors([]);
    }, []);

    useEffect(() => {
        if (!equipmentName) {
            setEquipmentName(targetNavEquipment.name);
            return;
        }
    }, [targetNavEquipment]);

    useEffect(() => {
        if (activeNavEquipment && activeNavEquipment.name) {

            setEquipmentName(activeNavEquipment.name);
        }
    }, [history.location.search]);

    const buildEntityNameTooltip = props => {
        return (
            <div>
                <div className="entityTooltipTitle">Equipment Information</div>
                <div className="entityTooltipHeader">Site</div>
                <div className="entityTooltipDetail">{activeNavSite.name}</div>
                <div className="entityTooltipHeader">Equipment Type</div>
                <div className="entityTooltipDetail">{props.equipmentType.name}</div>
                <div className="entityTooltipHeader">Manufacturer</div>
                <div className="entityTooltipDetail">{props.manufacturer ? props.manufacturer.name : ''}</div>
                <div className="entityTooltipHeader">Model</div>
                <div className="entityTooltipDetail">{props.model}</div>
                <div className="entityTooltipHeader">Serial Number</div>
                <div className="entityTooltipDetail">{props.serialNumber}</div>
                {
                    props.extendedData?.systems?.length > 0 ?
                        <div>
                            <div className="entityTooltipHeader">Associated Systems</div>
                            <div className="entityTooltipDetail">{props.extendedData.systems.map(s => s.name).join(', ')}</div>
                        </div>
                        : null
                }
            </div>
        );
    };
    
    return (
        <BobPage>
            <div className={classes.ClientPage}>
                <div className={classes.BreadcrumbPanel}>
                    <Breadcrumb />
                </div>
                <div className={classes.HeaderPanel}>
                    <div className={classes.NavigationPanel}>
                        <Navigation />
                    </div>
                    <div className={classes.Date}>
                        {
                            activeTab === 'Health Monitor' ?
                                null
                                :
                                <BobTimeStampFilter
                                    className={classes.TimestampOverride}
                                    maxDate={moment().local()}
                                    disableIntervalSelector={activeTab === 'Sensors' ? false : true}
                                />
                        }
                    </div>
                </div>
                <div className={classes.TitlePanel}>
                    <div className={classes.Name}>
                        {equipmentName}
                        {activeNavEquipment.name ?
                            <HtmlTooltip
                                information={buildEntityNameTooltip(activeNavEquipment)}
                                placement={'right-start'}
                                body={
                                    <div className={'infoIcon'}><span className="material-icons-outlined md-16" style={{ cursor: 'default' }}>info</span></div>
                                }
                            />
                            : null
                        }
                    </div>
                </div>
                {
                    process.env.dashboardKpiEnabled ?
                        <div className={classes.DataPanel}>
                            <KpiPanel
                                title="Score (% Change)"
                                data={renderScoreData(equipmentKpis, 'Equipment', dashboardIsCurrentTimeStamp)}
                                type="score"
                                className={classes.PercentChange}
                                information="Equipment Score Information"
                            />
                            <KpiPanel
                                title="Faults"
                                data={renderFaultData(equipmentKpis)}
                                kpiType={'fault'}
                            />
                        </div>
                        :
                        null
                }
                <div className={classes.TablePanel}>
                    <TableContainer
                        activeTab={activeTab}
                        axes={axes}
                        refreshTab={refreshTab}
                        setRefreshTab={(tab) => setRefreshTab(tab)}
                        setAxes={setAxes}
                        setActiveTab={setActiveTab}
                        updateTimeStamp={updateTimeStamp}
                    />
                </div>
            </div>
        </BobPage>
    );
};

const mapStateToProps = state => {
    return {
        activeConfiguration:         state.configurationData.activeConfiguration,
        activeNavCompany:            state.bob.activeNavCompany,
        activeNavEquipment:          state.bob.activeNavEquipment,
        activeNavSite:               state.bob.activeNavSite,
        allSensors:                  state.bob.allSensors,
        companyInfo:                 state.companyInfo,
        dashboardEndTimeStamp:       state.dashboard.dashboardEndTimeStamp ? state.dashboard.dashboardEndTimeStamp.format() : null,
        dashboardIsCurrentTimeStamp: state.dashboard.dashboardIsCurrentTimeStamp,
        dashboardStartTimeStamp:     state.dashboard.dashboardStartTimeStamp ? state.dashboard.dashboardStartTimeStamp.format() : null, 
        dashboardTimeframeEnd:       state.dashboard.dashboardTimeframeEnd ? state.dashboard.dashboardTimeframeEnd.format() : null,
        dashboardTimeframeStart:     state.dashboard.dashboardTimeframeStart ? state.dashboard.dashboardTimeframeStart.format() : null,
        dashboardTimePeriod:         state.dashboard.dashboardTimePeriod,
        desiredInterval:             state.dashboard.desiredInterval,
        equipmentKpis:               state.dashboard.equipmentKpis,
        equipmentTabIndex:           state.dashboard.equipmentTabIndex,
        faultHistoryMeta:            state.bob.faultHistoryMeta,
        selectedRule:                state.dashboard.selectedRule,
        selectedRuleSensors:         state.dashboard.selectedRuleSensors,
        selectedSensors:             state.dashboard.selectedSensors,
        selectedWeatherSensors:      state.dashboard.selectedWeatherSensors,
        sensorConfiguration:         state.dashboard.sensorConfiguration,
        targetNavEquipment:          state.bob.targetNavEquipment,
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...DashboardActions,
    ...bobActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EquipmentPage));
